import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { InkBlock } from '@/components/atoms';
import { Field } from '@/components/molecules';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { dwrClassesById } from '@/data/classes';
import type { Bond } from '@/schemas/bond';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';

const CUSTOM_BOND_VALUE = '__custom__' as const;

/**
 * Step 10 — Identità + Legami.
 *
 * Re-design Fase 2 PR 6: `<Field>` molecule per nome / titolo / testo legame
 * / nome PG bersaglio + `<InkBlock variant="full">Legami</InkBlock>` come
 * header. Slot legame rimane page-specific (Select template + textarea text
 * + input target name).
 *
 * **FI-25 (legami opzionali)**: bottone «Compilerò i legami in prima sessione»
 * imposta `draft.bondsDeferred = true`. Sezione completa anche con 0 bond.
 * **FI-26 (legami custom + testo editabile)**: ogni slot può scegliere un
 * template della classe o "Custom" (testo libero).
 *
 * Pattern bond-blank inline (input _____ substitution) del mockup → FI dedicato
 * (decisione H1 del WIP). DWR mantiene textarea full-text editabile.
 */
export function SectionIdentityBonds() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();
  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;

  function handleNameChange(name: string) {
    dispatch({ type: 'SET_NAME', name });
  }

  function handleTitleChange(title: string) {
    dispatch({ type: 'SET_TITLE', title: title.length > 0 ? title : undefined });
  }

  function handleBondAtSlot(slotIndex: number, partial: Partial<Bond> | null) {
    const next = [...draft.bonds];
    if (partial === null) {
      next[slotIndex] = undefined as unknown as Bond;
    } else {
      const current = next[slotIndex];
      next[slotIndex] = {
        text: partial.text ?? current?.text ?? '',
        targetCharacterName: partial.targetCharacterName ?? current?.targetCharacterName,
        resolved: false,
      };
    }
    const filtered = next.filter((b): b is Bond => Boolean(b) && b.text.length > 0);
    dispatch({ type: 'SET_BONDS', bonds: filtered });
  }

  function handleToggleDeferred() {
    dispatch({ type: 'MARK_BONDS_DEFERRED', bondsDeferred: !draft.bondsDeferred });
  }

  if (!classDef) {
    return (
      <p className="text-ink-soft italic">
        Seleziona prima una classe nello step precedente per impostare nome, titolo e legami.
      </p>
    );
  }

  const templates = classDef.bondTemplates;
  const maxBonds = classDef.maxBonds;
  const deferred = draft.bondsDeferred === true;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={
            <>
              Nome del personaggio <span className="text-accent-dwr">*</span>
            </>
          }
          htmlFor="pg-name"
        >
          <input
            id="pg-name"
            type="text"
            value={draft.name ?? ''}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="es. Veg"
          />
        </Field>
        <Field label="Titolo (opzionale)" htmlFor="pg-title">
          <input
            id="pg-title"
            type="text"
            value={draft.title ?? ''}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="es. Re dei Ladri"
          />
        </Field>
      </div>

      <BondsBlock
        deferred={deferred}
        maxBonds={maxBonds}
        templates={templates}
        bonds={draft.bonds}
        onToggleDeferred={handleToggleDeferred}
        onBondChange={handleBondAtSlot}
      />
    </div>
  );
}

interface BondsBlockProps {
  deferred: boolean;
  maxBonds: number;
  templates: readonly string[];
  bonds: readonly Bond[];
  onToggleDeferred: () => void;
  onBondChange: (slotIndex: number, partial: Partial<Bond> | null) => void;
}

// page-specific, not reusable: header + toolbar deferred + lista BondSlot.
//
// **State locale `customSlots`**: tiene traccia degli slot per cui l'utente
// ha esplicitamente selezionato "Custom" anche se non ha ancora scritto
// nessun testo. Il draft.bonds filtra via i bond con `text.length === 0`,
// quindi senza questo state un nuovo slot Custom vuoto rimbalzerebbe al
// primo template di default al re-render.
function BondsBlock({
  deferred,
  maxBonds,
  templates,
  bonds,
  onToggleDeferred,
  onBondChange,
}: BondsBlockProps) {
  const [customSlots, setCustomSlots] = useState<Set<number>>(new Set());

  function markCustom(slotIndex: number, custom: boolean) {
    setCustomSlots((prev) => {
      const next = new Set(prev);
      if (custom) next.add(slotIndex);
      else next.delete(slotIndex);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <InkBlock variant="full" className="flex-1">
          Legami
          <span className="text-paper-bright/70 ml-2 text-xs italic normal-case tracking-normal">
            (fino a {maxBonds}; ne basta uno per finalizzare)
          </span>
        </InkBlock>
        <button
          type="button"
          onClick={onToggleDeferred}
          className={
            deferred
              ? 'font-display bg-ink text-paper-bright cursor-pointer border-[1.5px] border-ink px-3 py-1.5 text-xs tracking-[0.08em] uppercase transition'
              : 'font-display border-ink-soft text-ink hover:bg-paper-soft border bg-transparent px-3 py-1.5 text-xs tracking-[0.08em] uppercase transition'
          }
        >
          {deferred ? 'Compilo ora' : 'Compilerò in prima sessione'}
        </button>
      </div>

      {deferred ? (
        <p className="border-rule-strong bg-paper-soft text-ink-soft border border-dashed p-3 text-sm italic">
          Hai scelto di compilare i legami in prima sessione, quando saprai con chi giocare. Il flag
          rimane sulla scheda finalizzata: il GM potrà ricordartelo al tavolo.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {Array.from({ length: maxBonds }).map((_, slotIndex) => (
            <BondSlot
              key={slotIndex}
              slotIndex={slotIndex}
              templates={templates}
              bond={bonds[slotIndex]}
              forcedCustom={customSlots.has(slotIndex)}
              onChange={(partial) => onBondChange(slotIndex, partial)}
              onCustomModeChange={(custom) => markCustom(slotIndex, custom)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Determina quale opzione del Select è "selezionata" dato l'attuale `bond`.
 *
 * - text vuoto → primo template disponibile (default slot), o Custom se la
 *   classe non ha template per quello slot.
 * - text === template originale → quel template.
 * - text === template.replace('_____', targetName) → quel template
 *   (sostituzione resa lato editor — PR 11 follow-up).
 * - altrimenti → Custom.
 */
function deduceSelectedValue(
  text: string,
  targetName: string,
  slotIndex: number,
  templates: readonly string[],
): string {
  if (text.length === 0) {
    return templates[slotIndex] ? String(slotIndex) : CUSTOM_BOND_VALUE;
  }
  const tplIndex = templates.findIndex((t) => {
    if (t === text) return true;
    if (targetName.length > 0 && t.replace(/_____+/g, targetName) === text) return true;
    return false;
  });
  return tplIndex >= 0 ? String(tplIndex) : CUSTOM_BOND_VALUE;
}

interface BondSlotProps {
  slotIndex: number;
  templates: readonly string[];
  bond: Bond | undefined;
  /**
   * Se `true`, lo slot è in modalità Custom anche se `bond` è undefined o
   * text vuoto (l'utente ha cliccato Custom ma non ha scritto nulla).
   */
  forcedCustom: boolean;
  onChange: (partial: Partial<Bond> | null) => void;
  onCustomModeChange: (custom: boolean) => void;
}

// page-specific, not reusable: 1 slot legame (Select template + input nome
// PG legato + textarea custom on-demand). 1 sola istanza nel SectionIdentityBonds,
// complessità interna, niente promote a molecule shared (rule of three non
// soddisfatta).
//
// **PR 11 follow-up — UX rivista**: per i template predefiniti, l'utente
// compila SOLO il nome del personaggio legato e la sostituzione del
// placeholder `_____` avviene lato editor (preview italica sotto). Per
// "Custom (testo libero)" l'utente vede invece la textarea piena per
// scrivere liberamente. Migliora leggibilità: niente textarea ridondante
// nei template (l'utente sostituisce manualmente _____, errori comuni).
function BondSlot({
  slotIndex,
  templates,
  bond,
  forcedCustom,
  onChange,
  onCustomModeChange,
}: BondSlotProps) {
  const text = bond?.text ?? '';
  const targetName = bond?.targetCharacterName ?? '';

  const isCustom =
    forcedCustom ||
    deduceSelectedValue(text, targetName, slotIndex, templates) === CUSTOM_BOND_VALUE;
  const selectedValue = isCustom
    ? CUSTOM_BOND_VALUE
    : deduceSelectedValue(text, targetName, slotIndex, templates);

  function applyTemplate(tplIndex: number, name: string) {
    const tplText = templates[tplIndex];
    if (tplText === undefined) return;
    const resolvedText = name.length > 0 ? tplText.replace(/_____+/g, name) : tplText;
    onChange({
      text: resolvedText,
      ...(name.length > 0 ? { targetCharacterName: name } : { targetCharacterName: undefined }),
    });
  }

  function handleTypeChange(value: string) {
    if (value === CUSTOM_BOND_VALUE) {
      onChange(null); // svuota text del bond, l'utente compilerà textarea
      onCustomModeChange(true);
      return;
    }
    onCustomModeChange(false);
    applyTemplate(Number(value), targetName);
  }

  function handleTargetChange(newTarget: string) {
    if (isCustom) {
      // Custom: aggiorno solo il nome, lascio text invariato.
      if (text.length === 0) return;
      onChange({
        text,
        ...(newTarget.length > 0
          ? { targetCharacterName: newTarget }
          : { targetCharacterName: undefined }),
      });
      return;
    }
    // Template: ricalcola text con sostituzione _____.
    applyTemplate(Number(selectedValue), newTarget);
  }

  function handleCustomTextChange(newText: string) {
    if (newText.length === 0) {
      onChange(null);
      return;
    }
    onChange({
      text: newText,
      ...(targetName.length > 0
        ? { targetCharacterName: targetName }
        : { targetCharacterName: undefined }),
    });
  }

  // **FI-70**: handler dedicato per rimozione totale del bond (template e
  // Custom uniformati). Chiama `onChange(null)` + reset `customSlots` del
  // parent (esce dalla modalità Custom forced). Visibile solo se il bond è
  // popolato (`text.length > 0`): altrimenti non c'è nulla da rimuovere.
  function handleRemoveBond() {
    onChange(null);
    onCustomModeChange(false);
  }

  return (
    <div className="border-rule bg-paper-bright flex flex-col gap-3 border p-3">
      <BondTypeSelect
        slotIndex={slotIndex}
        templates={templates}
        selectedValue={selectedValue}
        onChange={handleTypeChange}
      />
      <BondTargetField
        slotIndex={slotIndex}
        isCustom={isCustom}
        targetName={targetName}
        onChange={handleTargetChange}
      />
      <BondBodySlot
        isCustom={isCustom}
        text={text}
        slotIndex={slotIndex}
        onCustomTextChange={handleCustomTextChange}
      />
      {text.length > 0 ? (
        <button
          type="button"
          onClick={handleRemoveBond}
          aria-label={`Rimuovi legame slot ${slotIndex + 1}`}
          title="Rimuovi questo legame"
          className="font-display border-accent-dwr text-accent-dwr hover:bg-paper-soft inline-flex items-center gap-1 self-end border px-2 py-1 text-[0.7rem] tracking-[0.08em] uppercase transition"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          Rimuovi
        </button>
      ) : null}
    </div>
  );
}

interface BondTypeSelectProps {
  slotIndex: number;
  templates: readonly string[];
  selectedValue: string;
  onChange: (value: string) => void;
}

function BondTypeSelect({ slotIndex, templates, selectedValue, onChange }: BondTypeSelectProps) {
  return (
    <Field label={`Tipo di legame · slot ${slotIndex + 1}`}>
      <Select value={selectedValue} onValueChange={onChange}>
        <SelectTrigger className="w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {templates.map((tpl, i) => (
            <SelectItem key={i} value={String(i)}>
              Suggerito {i + 1}: <em className="ml-1 truncate">{tpl}</em>
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM_BOND_VALUE}>Custom (testo libero)</SelectItem>
        </SelectContent>
      </Select>
    </Field>
  );
}

interface BondTargetFieldProps {
  slotIndex: number;
  isCustom: boolean;
  targetName: string;
  onChange: (newTarget: string) => void;
}

function BondTargetField({ slotIndex, isCustom, targetName, onChange }: BondTargetFieldProps) {
  return (
    <Field
      label={isCustom ? 'Nome del personaggio legato (opzionale)' : 'Nome del personaggio legato'}
      htmlFor={`bond-target-${slotIndex}`}
      hint={
        isCustom
          ? undefined
          : 'Il nome verrà inserito automaticamente al posto del placeholder del legame.'
      }
    >
      <input
        id={`bond-target-${slotIndex}`}
        type="text"
        value={targetName}
        onChange={(e) => onChange(e.target.value)}
        placeholder="es. Veg"
      />
    </Field>
  );
}

interface BondBodySlotProps {
  isCustom: boolean;
  text: string;
  slotIndex: number;
  onCustomTextChange: (text: string) => void;
}

// Per Custom mostra la textarea editabile; per template mostra la preview
// con sostituzione _____ già applicata (read-only blockquote).
function BondBodySlot({ isCustom, text, slotIndex, onCustomTextChange }: BondBodySlotProps) {
  if (isCustom) {
    return (
      <Field
        label="Testo del legame"
        htmlFor={`bond-text-${slotIndex}`}
        hint="Scrivi liberamente il tuo legame."
      >
        <textarea
          id={`bond-text-${slotIndex}`}
          rows={2}
          value={text}
          onChange={(e) => onCustomTextChange(e.target.value)}
          placeholder="Scrivi liberamente il tuo legame…"
        />
      </Field>
    );
  }
  if (text.length === 0) return null;
  return (
    <blockquote className="border-l-rule-strong text-ink-soft font-serif border-l-2 pl-3 text-[0.95rem] italic">
      {text}
    </blockquote>
  );
}
